import { useState, useEffect, useCallback } from 'react';
import { apiFetch, API_BASE } from '../../lib/api';

// ── Types ──────────────────────────────────────────────────────────────────

interface DeploymentEnvironment {
  status:            'SUCCESS' | 'FAILED';
  liveUrl:           string;
  apiUrl:            string;
  instanceId:        string;
  publicIp?:         string;
  healthCheck?:      'passing' | 'failing' | 'unknown';
  responseTimeMs?:   number;
  deployedAt?:       string;
  isPromotionTarget: boolean;
  // DEPLOY.A2 PerEnvResult fields (optional — older records may omit)
  name?:                  string;
  deploymentEnvironment?: string;
}

interface DeploymentRecord {
  id: string;
  liveUrl: string;
  apiUrl: string;
  deployedAt: string;
  healthCheck: string;
  responseTimeMs: number;
  instanceId: string;
  region: string;
  deploymentType: string;
  version: string;
  status: 'LIVE' | 'SUPERSEDED' | 'FAILED' | 'ROLLED_BACK';
  // DEPLOY.A3 (#954, Q-DASHBOARD) — full per-env list. May be missing on
  // legacy records; fall back to single headline card in that case.
  environments?: DeploymentEnvironment[];
}

interface LiveHealthCheck {
  healthCheck: 'passing' | 'failing';
  responseTimeMs: number;
  checkedAt: string;
}

interface Props {
  projectId: string;
}

// ── Status badge colours ───────────────────────────────────────────────────

const STATUS_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  LIVE:        { bg: 'rgba(34,197,94,0.1)',  text: '#16a34a', dot: '#22c55e' },
  SUPERSEDED:  { bg: 'rgba(156,163,175,0.1)', text: '#6b7280', dot: '#9ca3af' },
  FAILED:      { bg: 'rgba(239,68,68,0.1)',   text: '#dc2626', dot: '#ef4444' },
  ROLLED_BACK: { bg: 'rgba(245,158,11,0.1)',  text: '#d97706', dot: '#f59e0b' },
};

function StatusBadge({ status }: { status: string }) {
  const colors = STATUS_COLORS[status] ?? STATUS_COLORS.SUPERSEDED;
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '2px 10px', borderRadius: 9999, fontSize: 12, fontWeight: 600,
        background: colors.bg, color: colors.text,
      }}
    >
      <span
        style={{
          width: 8, height: 8, borderRadius: '50%', background: colors.dot,
          ...(status === 'LIVE' ? { animation: 'pulse 2s infinite' } : {}),
        }}
      />
      {status}
    </span>
  );
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ── Environment list (DEPLOY.A3, #954) ─────────────────────────────────────
// Renders one row per environment. Promotion target gets a "Primary" badge
// and slightly heavier visual weight (accent border, larger title). Failed
// envs show in the same list with the FAILED badge.

function EnvironmentList({ environments }: { environments: DeploymentEnvironment[] }) {
  // Sort: promotion targets first, then by name.
  const sorted = [...environments].sort((a, b) => {
    if (a.isPromotionTarget !== b.isPromotionTarget) return a.isPromotionTarget ? -1 : 1;
    return (a.deploymentEnvironment ?? a.name ?? '').localeCompare(b.deploymentEnvironment ?? b.name ?? '');
  });

  return (
    <div
      data-testid="environment-list"
      style={{
        display: 'flex', flexDirection: 'column', gap: 8,
        margin: '0 0 16px',
        padding: 12,
        background: 'var(--surface-alt)',
        borderRadius: 8,
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>
        Environments ({environments.length})
      </div>
      {sorted.map((env, i) => {
        const envBadgeStatus = env.status === 'SUCCESS' ? 'LIVE' : 'FAILED';
        return (
          <div
            key={`${env.deploymentEnvironment ?? env.name ?? i}-${i}`}
            data-testid={`env-row-${env.deploymentEnvironment ?? i}`}
            style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: env.isPromotionTarget ? '10px 12px' : '6px 12px',
              borderLeft: env.isPromotionTarget
                ? '3px solid var(--primary)'
                : '3px solid transparent',
              background: 'var(--surface)',
              borderRadius: 6,
              fontSize: 14,
            }}
          >
            <StatusBadge status={envBadgeStatus} />
            <span
              style={{
                fontWeight: env.isPromotionTarget ? 600 : 400,
                fontSize: env.isPromotionTarget ? 14 : 13,
                color: 'var(--text)',
                minWidth: 90,
              }}
            >
              {env.deploymentEnvironment ?? env.name ?? '—'}
            </span>
            {env.isPromotionTarget && (
              <span
                style={{
                  padding: '1px 8px', borderRadius: 9999,
                  background: 'rgba(124,58,237,0.1)',
                  color: 'var(--primary)',
                  fontSize: 11, fontWeight: 600,
                }}
              >
                Primary
              </span>
            )}
            {env.status === 'SUCCESS' && env.liveUrl && (
              <a
                href={env.liveUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: 'var(--text-link, #2563eb)',
                  fontSize: 13, textDecoration: 'none',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  flex: 1,
                }}
              >
                {env.liveUrl} ↗
              </a>
            )}
            {env.status === 'FAILED' && (
              <span style={{ color: 'var(--text-secondary)', fontSize: 13, flex: 1, fontStyle: 'italic' }}>
                (deploy failed)
              </span>
            )}
            {env.responseTimeMs !== undefined && env.responseTimeMs > 0 && (
              <span style={{ color: 'var(--text-secondary)', fontSize: 12, minWidth: 50, textAlign: 'right' }}>
                {env.responseTimeMs}ms
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Component ──────────────────────────────────────────────────────────────

export default function DeploymentDashboard({ projectId }: Props) {
  const [deployments, setDeployments] = useState<DeploymentRecord[]>([]);
  const [liveCheck, setLiveCheck] = useState<LiveHealthCheck | null>(null);
  const [loading, setLoading] = useState(true);
  const [redeploying, setRedeploying] = useState(false);
  const [rollbackTarget, setRollbackTarget] = useState<DeploymentRecord | null>(null);
  const [rollbackReason, setRollbackReason] = useState('');
  const [rollbackLoading, setRollbackLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadDeployments = useCallback(async () => {
    try {
      const data = await apiFetch<{ deployments: DeploymentRecord[] }>(
        `/projects/${projectId}/deployments`,
      );
      setDeployments(data.deployments);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [projectId]);

  const loadLatest = useCallback(async () => {
    try {
      const data = await apiFetch<{ liveHealthCheck: LiveHealthCheck }>(
        `/projects/${projectId}/deployments/latest`,
      );
      setLiveCheck(data.liveHealthCheck);
    } catch { /* no deployment yet */ }
  }, [projectId]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadDeployments(), loadLatest()]).finally(() => setLoading(false));
  }, [loadDeployments, loadLatest]);

  const liveDeployment = deployments.find(d => d.status === 'LIVE');
  const previousDeployment = deployments.find(d => d.status === 'SUPERSEDED');

  const handleRedeploy = async () => {
    setRedeploying(true);
    setError(null);
    try {
      const token = localStorage.getItem('blueprint_token');
      const response = await fetch(`${API_BASE}/projects/${projectId}/deployments/trigger`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      const reader = response.body?.getReader();
      if (!reader) return;
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const text = decoder.decode(value);
        const lines = text.split('\n').filter(l => l.startsWith('data: '));
        for (const line of lines) {
          const event = JSON.parse(line.slice(6));
          if (event.type === 'deployment_completed' || event.type === 'deployment_failed') {
            await loadDeployments();
            await loadLatest();
          }
        }
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setRedeploying(false);
    }
  };

  const handleRollback = async () => {
    if (!rollbackTarget) return;
    setRollbackLoading(true);
    try {
      const result = await apiFetch<{ success: boolean; message: string }>(
        `/projects/${projectId}/deployments/rollback`,
        {
          method: 'POST',
          body: JSON.stringify({
            targetDeploymentId: rollbackTarget.id,
            reason: rollbackReason || 'Manual rollback',
          }),
        },
      );
      setRollbackTarget(null);
      setRollbackReason('');
      if (result.success) {
        await loadDeployments();
        await loadLatest();
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setRollbackLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)' }}>
        Loading deployments...
      </div>
    );
  }

  if (deployments.length === 0) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)' }}>
        No deployments yet. Start a deployment from the Phase 7 agent.
      </div>
    );
  }

  const cardStyle: React.CSSProperties = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    padding: 24,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>

      {error && (
        <div style={{ padding: '12px 16px', background: 'rgba(239,68,68,0.1)', color: '#dc2626', borderRadius: 8, fontSize: 14 }}>
          {error}
          <button onClick={() => setError(null)} style={{ marginLeft: 12, background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontWeight: 600 }}>Dismiss</button>
        </div>
      )}

      {/* Live deployment card */}
      {liveDeployment && (
        <div style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <StatusBadge status="LIVE" />
            <a
              href={liveDeployment.liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--text-link, #2563eb)', fontWeight: 500, fontSize: 15, textDecoration: 'none' }}
            >
              {liveDeployment.liveUrl} ↗
            </a>
          </div>

          {/* DEPLOY.A3 (#954, Q-DASHBOARD) — per-env list with promotion-
              target emphasized. Falls back to headline-only when the
              record predates DEPLOY.A3 (no .environments field). */}
          {liveDeployment.environments && liveDeployment.environments.length > 1 && (
            <EnvironmentList environments={liveDeployment.environments} />
          )}

          {liveDeployment.apiUrl && liveDeployment.apiUrl !== liveDeployment.liveUrl && (
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
              API: {liveDeployment.apiUrl}
            </div>
          )}

          <div style={{ display: 'flex', gap: 24, fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16, flexWrap: 'wrap' }}>
            <span>
              Health: {liveCheck?.healthCheck === 'passing' ? '✓ Passing' : '✗ Failing'}
              {liveCheck?.checkedAt && ` (checked ${timeAgo(liveCheck.checkedAt)})`}
            </span>
            {liveCheck && liveCheck.responseTimeMs > 0 && (
              <span>Response: {liveCheck.responseTimeMs}ms</span>
            )}
          </div>

          <div style={{ display: 'flex', gap: 24, fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
            <span>Deployed: {timeAgo(liveDeployment.deployedAt)}</span>
            <span>Version: {liveDeployment.version}</span>
            <span>{liveDeployment.deploymentType === 'MANAGED' ? 'ECS Fargate' : 'BYOC (EC2)'}</span>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={handleRedeploy}
              disabled={redeploying}
              style={{
                padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: 'var(--primary)', color: '#fff', fontWeight: 600, fontSize: 14,
                opacity: redeploying ? 0.6 : 1,
              }}
            >
              {redeploying ? 'Deploying...' : 'Redeploy main →'}
            </button>
            {previousDeployment && (
              <button
                onClick={() => setRollbackTarget(previousDeployment)}
                style={{
                  padding: '8px 20px', borderRadius: 8, cursor: 'pointer',
                  background: 'transparent', border: '1px solid var(--border)',
                  color: 'var(--text)', fontWeight: 500, fontSize: 14,
                }}
              >
                Roll back to previous
              </button>
            )}
          </div>
        </div>
      )}

      {/* Version history */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>
          Version history
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {deployments.map((d) => (
            <div
              key={d.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 16,
                padding: '10px 12px', borderRadius: 8,
                background: d.status === 'LIVE' ? 'rgba(34,197,94,0.04)' : 'transparent',
                fontSize: 14,
              }}
            >
              <StatusBadge status={d.status} />
              <span style={{ flex: 1, color: 'var(--text)' }}>
                {d.deploymentType === 'MANAGED' ? 'ECS' : 'BYOC'}
              </span>
              <span style={{ color: 'var(--text-secondary)', minWidth: 60 }}>
                {d.responseTimeMs > 0 ? `${d.responseTimeMs}ms` : '-'}
              </span>
              <span style={{ color: 'var(--text-secondary)', minWidth: 80, textAlign: 'right' }}>
                {timeAgo(d.deployedAt)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Rollback confirmation modal */}
      {rollbackTarget && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setRollbackTarget(null); }}
        >
          <div style={{ background: 'var(--surface)', borderRadius: 16, padding: 32, maxWidth: 480, width: '90%' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 600 }}>
              Confirm Rollback
            </h3>
            <p style={{ margin: '0 0 16px', color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.6 }}>
              Roll back to deployment from {timeAgo(rollbackTarget.deployedAt)}?
              This will update the live service to the previous version.
            </p>
            <textarea
              value={rollbackReason}
              onChange={(e) => setRollbackReason(e.target.value)}
              placeholder="Reason for rollback (optional)"
              style={{
                width: '100%', padding: '10px 12px', borderRadius: 8, fontSize: 14,
                border: '1px solid var(--border)', background: 'var(--surface-alt)',
                color: 'var(--text)', resize: 'vertical', minHeight: 60, marginBottom: 16,
                boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setRollbackTarget(null)}
                style={{
                  padding: '8px 20px', borderRadius: 8, cursor: 'pointer',
                  background: 'transparent', border: '1px solid var(--border)',
                  color: 'var(--text)', fontWeight: 500, fontSize: 14,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleRollback}
                disabled={rollbackLoading}
                style={{
                  padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: '#f59e0b', color: '#fff', fontWeight: 600, fontSize: 14,
                  opacity: rollbackLoading ? 0.6 : 1,
                }}
              >
                {rollbackLoading ? 'Rolling back...' : 'Confirm Rollback'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
